import { ChangeDetectionStrategy, Component, inject, input } from '@angular/core';
import { RouterLink } from '@angular/router';
import { SafeHtmlPipe } from '@/app/shared/ui/pipes/safe-html.pipe';
import { NavIcons } from '@/app/shared/nav/nav-icons';
import { NavSection } from '@/app/shared/nav/nav-section.model';
import { ShellStore } from './shell.store';

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [RouterLink, SafeHtmlPipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'block [grid-area:sidebar] min-w-0 min-h-0',
  },
  template: `
    <nav class="flex h-full flex-col overflow-hidden border-r border-border bg-surface">
      <!-- Drill-in header: aparece al navegar dentro de un sub-menú -->
      @if (store.drilledItem() !== null) {
        <div class="shrink-0 border-b border-border px-2.5 pt-2.5">
          <button
            class="flex w-full cursor-pointer items-center gap-1.5 rounded-xs border-0 bg-transparent px-2 py-1.5 text-left font-sans text-[13px] font-medium text-muted transition-colors duration-[120ms] hover:bg-surface-2 hover:text-primary"
            (click)="store.drillOut()"
            aria-label="Volver al menú principal"
          >
            <span
              class="flex items-center [&_svg]:h-3.5 [&_svg]:w-3.5"
              [innerHTML]="icons.chevronLeft | safeHtml"
              aria-hidden="true"
            ></span>
            Menú principal
          </button>
          <span
            class="block px-3 pt-2 pb-2.5 text-[11px] font-bold tracking-[0.08em] text-text uppercase"
            >{{ store.drilledItem()?.drillTitle ?? store.drilledItem()?.label }}</span
          >
        </div>
      }

      <!-- Navigation body -->
      <div class="min-h-0 flex-1 overflow-y-auto overflow-x-hidden px-3.5 pt-4">
        @if (store.drilledItem() === null) {
          <!-- ── MENÚ PRINCIPAL ── -->
          <div [class]="store.animDir() === 'out' ? 'animate-[slideFromLeft_180ms_ease]' : ''">
            @for (section of sections(); track section.title) {
              <div class="pb-5">
                <span
                  class="block px-3 pb-2 text-[11px] font-semibold tracking-[0.08em] text-muted uppercase"
                  >{{ section.title }}</span
                >
                @for (item of section.items; track item.label) {
                  @if (item.expandable && item.children?.length) {
                    <button
                      [class]="groupClass(store.isItemActive(item))"
                      (click)="store.toggleExpanded(item)"
                      [attr.aria-expanded]="store.isExpanded(item)"
                    >
                      <span class="flex-1 overflow-hidden text-ellipsis">{{ item.label }}</span>
                      <span
                        [class]="expandChevronClass(store.isExpanded(item))"
                        [innerHTML]="icons.chevronDown | safeHtml"
                        aria-hidden="true"
                      ></span>
                    </button>
                    @if (store.isExpanded(item)) {
                      <div
                        class="flex flex-col gap-0.5 pb-1"
                        role="group"
                        [attr.aria-label]="item.label"
                      >
                        @for (child of item.children; track child.label) {
                          <a
                            [class]="subItemClass(store.isItemActive(child))"
                            [routerLink]="child.route"
                          >
                            <span class="flex-1 overflow-hidden text-ellipsis">{{
                              child.label
                            }}</span>
                          </a>
                        }
                      </div>
                    }
                  } @else if (item.children?.length) {
                    <button
                      [class]="itemClass(store.isItemActive(item))"
                      (click)="store.drillIn(item)"
                      aria-haspopup="true"
                    >
                      <span class="flex-1 overflow-hidden text-ellipsis">{{ item.label }}</span>
                      <span
                        [class]="chevronClass(store.isItemActive(item))"
                        [innerHTML]="icons.chevronRight | safeHtml"
                        aria-hidden="true"
                      ></span>
                    </button>
                  } @else {
                    <a [class]="itemClass(store.isItemActive(item))" [routerLink]="item.route">
                      <span class="flex-1 overflow-hidden text-ellipsis">{{ item.label }}</span>
                    </a>
                  }
                }
              </div>
            }

            @if (!sections().length) {
              <p class="m-0 px-5 py-4 text-[13px] text-muted">Sin secciones configuradas</p>
            }
          </div>
        } @else {
          <!-- ── SUB-MENÚ ── -->
          <div [class]="store.animDir() === 'in' ? 'animate-[slideFromRight_180ms_ease]' : ''">
            @for (item of store.drilledItem()!.children ?? []; track item.label) {
              @if (item.children?.length) {
                <button
                  [class]="itemClass(store.isItemActive(item))"
                  (click)="store.drillIn(item)"
                  aria-haspopup="true"
                >
                  <span class="flex-1 overflow-hidden text-ellipsis">{{ item.label }}</span>
                  <span
                    [class]="chevronClass(store.isItemActive(item))"
                    [innerHTML]="icons.chevronRight | safeHtml"
                    aria-hidden="true"
                  ></span>
                </button>
              } @else {
                <a [class]="itemClass(store.isItemActive(item))" [routerLink]="item.route">
                  <span class="flex-1 overflow-hidden text-ellipsis">{{ item.label }}</span>
                </a>
              }
            }
          </div>
        }
      </div>
    </nav>
  `,
  styles: [
    `
      @keyframes slideFromRight {
        from {
          transform: translateX(18px);
          opacity: 0;
        }
        to {
          transform: translateX(0);
          opacity: 1;
        }
      }
      @keyframes slideFromLeft {
        from {
          transform: translateX(-18px);
          opacity: 0;
        }
        to {
          transform: translateX(0);
          opacity: 1;
        }
      }
    `,
  ],
})
export class SidebarComponent {
  readonly sections = input<NavSection[]>([]);
  protected readonly store = inject(ShellStore);
  protected readonly icons = NavIcons;

  protected itemClass(active: boolean): string {
    const base =
      'group flex w-full cursor-pointer items-center gap-3 whitespace-nowrap rounded-sm border-0 bg-transparent px-3 py-[9px] text-left font-sans text-[15px] font-semibold no-underline transition-colors duration-[120ms]';
    return active
      ? `${base} !bg-primary text-white`
      : `${base} text-text hover:bg-surface-2 hover:text-primary`;
  }

  protected groupClass(active: boolean): string {
    const base =
      'flex w-full cursor-pointer items-center gap-3 whitespace-nowrap rounded-sm border-0 bg-transparent px-3 py-[9px] text-left font-sans text-[15px] font-semibold transition-colors duration-[120ms] hover:bg-surface-2';
    return active ? `${base} text-primary` : `${base} text-text hover:text-primary`;
  }

  protected subItemClass(active: boolean): string {
    const base =
      'flex w-full items-center whitespace-nowrap rounded-sm py-2 pr-3 pl-7 font-sans text-[15px] font-medium no-underline transition-colors duration-[120ms]';
    return active
      ? `${base} !bg-primary text-white`
      : `${base} text-text/85 hover:bg-surface-2 hover:text-primary`;
  }

  protected expandChevronClass(expanded: boolean): string {
    const base =
      'ml-auto flex shrink-0 items-center opacity-50 transition-transform duration-[120ms] [&_svg]:h-3.5 [&_svg]:w-3.5';
    return expanded ? `${base} rotate-180` : base;
  }

  protected chevronClass(active: boolean): string {
    const base =
      'ml-auto flex shrink-0 items-center transition-[opacity,transform] duration-[120ms] [&_svg]:h-3.5 [&_svg]:w-3.5';
    return active
      ? `${base} opacity-80`
      : `${base} opacity-35 group-hover:translate-x-0.5 group-hover:opacity-70`;
  }
}
